import React, { CSSProperties, FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { mix } from 'polished';
import { asStyledComponent } from '@cld/component-enhancer';
import { typographyMixin, TypographySize } from '@cld/typography';
import { CommonComponentProps, StylableComponentProps } from '@cld/common-definitions';
import { combineValues } from '@cld/theme-helper';
import { theme, sizeVariant } from './theme';

export interface ChipProps extends CommonComponentProps<'div'>, StylableComponentProps {
  /** Chip label */
  label: ReactNode;
  /** Left icon slot */
  leftSlot?: ReactNode;
  /** Right icon slot */
  rightSlot?: ReactNode;
  /** Chip size */
  size?: 'md' | 'sm';
  /** Add hover styles **/
  isHoverable?: boolean;
  /** Custom CSS styles **/
  style?: CSSProperties;
  /** onClick handler */
  onClick?: () => void;
  dataTest?: string;
}

interface RootProps {
  isHoverable: ChipProps['isHoverable'];
  size: Exclude<ChipProps['size'], undefined>;
}

export const Root = styled.div<RootProps>`
  display: inline-flex;
  /* fix for safari improperly measuring children height */
  min-height: -webkit-fit-content;
  align-items: center;
  background-color: ${theme.background.color};
  border-radius: ${theme.radius};
  ${(props: RootProps) =>
    typographyMixin({
      ...props,
      size: { md: 'xs', sm: 'xxs' }[props.size] as TypographySize,
      type: 'regular',
    })};
  padding: ${sizeVariant((branch) => `${branch.padding.vertical} ${branch.padding.horizontal}`)};
  white-space: nowrap;
  cursor: default;
  
  ${(props) => props.isHoverable && css`
    cursor: pointer;
    transition: background-color 200ms;
    
    &:hover {
      background-color: ${combineValues(theme.background.color, theme.background.contrast, (color, overlayColor) =>
        mix(0.06, overlayColor, color)
      )};
    }
  `}
`;

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${theme.leftSlotSpacing};

  &:empty {
    display: none;
  }
`;

const RightWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${theme.rightSlotSpacing};

  &:empty {
    display: none;
  }
`;

/** The purpose of this component is to display text within a small chip / pill. May be wrapped with `ChipList` */
export const Chip = asStyledComponent<FC<ChipProps>>(
  ({ size = 'md', isHoverable = false, style, label, className, leftSlot, rightSlot, rootProps, onClick, dataTest }) => (
    <Root onClick={onClick} size={size} isHoverable={isHoverable} className={className} {...rootProps} style={style} data-test={dataTest}>
      <LeftWrapper>{leftSlot}</LeftWrapper>
      {label}
      <RightWrapper>{rightSlot}</RightWrapper>
    </Root>
  )
);
